import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Post } from "./protocol.ts";

export function AttachmentComposer({ roomKey, online, text, setText, pending, send }: { roomKey: string; online: boolean; text: string; setText: (text: string) => void; pending: boolean; send: (event: FormEvent) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const controller = useRef<AbortController | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const messageInput = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { const element = messageInput.current; if (element) { element.style.height = "auto"; element.style.height = `${element.scrollHeight}px`; } }, [text]);
  const [preview, setPreview] = useState("");
  const locked = busy || pending || !online;
  useEffect(() => {
    if (!file || !["image/png", "image/jpeg", "image/webp"].includes(file.type)) { setPreview(""); return; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => { if (!online) { controller.current?.abort(); setFile(null); } }, [online]);
  function choose(value: File | undefined) {
    setError("");
    if (!value) return;
    if (!value.size || value.size > 5 * 1024 * 1024) { setError("Escolha um arquivo não vazio de até 5 MB."); return; }
    setFile(value);
  }
  useEffect(() => {
    const textarea = document.getElementById("message");
    function paste(event: ClipboardEvent) {
      const files = event.clipboardData?.files;
      if (!files?.length) return;
      event.preventDefault();
      if (!locked) choose(files[0]);
    }
    textarea?.addEventListener("paste", paste);
    return () => textarea?.removeEventListener("paste", paste);
  }, [locked]);
  async function upload() {
    if (!file || locked) return;
    setBusy(true); setError("");
    const abort = new AbortController(); controller.current = abort;
    const timer = setTimeout(() => abort.abort(), 60000);
    try {
      const response = await fetch(`/api/rooms/${roomKey}/attachments`, { method: "POST", headers: { "Content-Type": "application/octet-stream", "X-File-Name": encodeURIComponent(file.name.slice(0, 180)), "X-Message": btoa(String.fromCharCode(...new TextEncoder().encode(text.trim()))) }, body: file, signal: abort.signal });
      if (abort.signal.aborted) return;
      if (!response.ok) { const body = await response.json().catch(() => ({})); throw new Error(body.error || "Não foi possível enviar o arquivo."); }
      setFile(null); setText("");
    } catch (error) { setError(error instanceof Error && error.name !== "AbortError" ? error.message : "Envio interrompido. Confira o chat antes de tentar novamente."); }
    finally { clearTimeout(timer); setBusy(false); }
  }
  return <form className="message-composer" aria-busy={busy || pending} onSubmit={event => { event.preventDefault(); if (locked) return; if (file) void upload(); else send(event); }}>
    {file && <div className="attachment-chip">{preview && <img src={preview} alt="Prévia do anexo" onError={() => setPreview("")} />}<span><strong>{file.name}</strong><small>{(file.size / 1024).toFixed(0)} KB</small></span><button type="button" aria-label="Remover anexo" disabled={busy} onClick={() => setFile(null)}>×</button></div>}
    <label className="sr-only" htmlFor="message">Mensagem para o grupo</label>
    <textarea ref={messageInput} id="message" placeholder="Escreva ou cole uma imagem…" value={text} maxLength={2000} disabled={locked} onChange={event => setText(event.target.value)} />
    <input ref={picker} type="file" hidden disabled={locked} onChange={event => { choose(event.target.files?.[0]); event.target.value = ""; }} />
    <div className="composer-actions"><button type="button" className="attach-button" title="Anexar arquivo ou imagem · até 5 MB" aria-label="Anexar arquivo ou imagem, até 5 MB" disabled={locked} onClick={() => picker.current?.click()}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21 11-8.5 8.5a6 6 0 0 1-8.5-8.5L13 2a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8l8.5-8.5" /></svg></button><small>{text.length}/2000</small><button className="primary" disabled={locked || (!file && !text.trim())}>{busy || pending ? "Enviando…" : "Enviar ↗"}</button></div>
    <small className="composer-help">Até 5 MB · anexos temporários na sala</small>
    {error && <p role="alert">{error}</p>}
  </form>;
}
export function AttachmentView({ roomKey, attachment }: { roomKey: string; attachment: NonNullable<Post["attachment"]> }) {
  const url = `/api/rooms/${roomKey}/attachments/${attachment.id}`;
  const [failed, setFailed] = useState(false);
  return <div className="chat-attachment">{attachment.image && !failed && <img src={url} alt={attachment.name} loading="lazy" onError={() => setFailed(true)} />}{failed && <p>Prévia indisponível.</p>}<a href={url + "?download=1"} download>{attachment.name} · {(attachment.size / 1024).toFixed(0)} KB — baixar</a></div>;
}
